<script lang="ts">
  import type { Store } from '../state/store.svelte';
  import {
    selectBodyView,
    selectLegendRows,
    selectOrbitalReadouts,
    selectPhaseOffset,
    selectSectorSettings,
    MIN_SECTOR_COUNT,
    MAX_SECTOR_COUNT,
    ECCENTRICITY_MIN,
    ECCENTRICITY_MAX
  } from '../state/selectors';
  import { PHASE_STEP_RADIANS } from '../state/phase';
  import { describeCentreSwitch } from './centre-switch';
  import {
    formatAu,
    formatKmPerSec,
    formatPeriod,
    describeOrbitShape,
    metresToAu,
    auToMetres,
    MIN_SEMI_MAJOR_AXIS_AU,
    MAX_SEMI_MAJOR_AXIS_AU,
    SEMI_MAJOR_AXIS_RANGE_MESSAGE
  } from './format';

  // Selected-body detail panel (right-docked). Story 1.3 adds the eccentricity
  // control and the SI orbital readouts; Story 1.4 adds the semi-major-axis editor
  // and the orbital-period readout. It imports ONLY from `src/state/` (selectors +
  // store) and `./format`; no `core` import in ui (AD-2 layer table). All unit
  // conversion (AU, days/years, km/s) happens at the readout boundary (AD-5), via
  // `./format`, which sources the single AU factor through the `state` re-export.
  let {
    store,
    announce
  }: { store: Store; announce?: (message: string) => void } = $props();

  let collapsed = $state(false);

  const selected = $derived(selectBodyView(store.state, store.state.selectedBody));
  const central = $derived(selectBodyView(store.state, store.state.centralBody));
  // Every body, in legend order, for the always-visible central-body control
  // (Story 2.2, AC 1): the learner may pick any of the nine as the centre.
  const allBodies = $derived(selectLegendRows(store.state));
  const readouts = $derived(selectOrbitalReadouts(store.state, store.state.selectedBody));
  const isCentral = $derived(readouts.isCentral);
  // Story 4.1 (AC 5, 6): a removed (collided/absorbed) body stays selected but its
  // orbital controls go read-only. The body is never auto-deselected; the learner may
  // still select any surviving body via the legend. The semi-major-axis and
  // eccentricity editors gate on this in addition to `isCentral` (the sector, phase,
  // and central-body controls already exclude a non-active body).
  const isRemoved = $derived(selected.status !== 'active');
  const isReadOnly = $derived(isCentral || isRemoved);

  // Phase manipulation (Story 1.7, FR-19) applies only to a selected NON-central,
  // active body (AC 8): the central body does not orbit, and a removed
  // (collided/absorbed) body has no live phase to step.
  const phaseControllable = $derived(!isCentral && selected.status === 'active');
  // Session-only dragged phase offset (radians), exposed as a data attribute for
  // e2e evidence that a step/drag changed the phase. Never shared (AD-4).
  const phaseOffset = $derived(selectPhaseOffset(store.state, store.state.selectedBody));

  // Step the selected body's phase by the ONE shared increment (AD-2), forward
  // (+1) or back (-1). Raises the identical `NudgePhase` command as the drag,
  // differing only in that the magnitude is the fixed increment. Announces via the
  // live region in GCSE terms, without moving focus (AD-8).
  function stepPhase(direction: 1 | -1): void {
    if (!phaseControllable) return;
    store.dispatch({
      type: 'NudgePhase',
      body: store.state.selectedBody,
      deltaMeanAnomaly: direction * PHASE_STEP_RADIANS
    });
    announce?.(`Stepped ${selected.name} ${direction > 0 ? 'forward' : 'back'} along its orbit.`);
  }

  // Left/Right arrow keys step back/forward, active ONLY within the phase-step
  // control group (scoped to its focus), never bound on the canvas, so this never
  // collides with Story 3.2's canvas pan on arrow keys.
  function onPhaseKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      stepPhase(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      stepPhase(1);
    }
  }

  // The single central-body switch path shared by the "Make centre" button and the
  // "Central body" control (Story 2.2). All three switch entry points (these two
  // plus the drop-on-centre drag in CanvasHost) raise the identical `SwitchCentre`
  // command, so they cannot diverge (AC 3, AD-2). Capture the previous-centre name
  // BEFORE dispatch (after it, `state.centralBody` is already the new centre) and
  // announce the success only when the centre actually changed to an active body
  // (AC 9). No try/catch: the 2.2 reducer no longer throws.
  function switchCentre(bodyId: string): void {
    const prevCentre = store.state.centralBody;
    if (bodyId === prevCentre) return;
    const target = store.state.bodies.find((b) => b.id === bodyId);
    if (!target || target.status !== 'active') return;
    const prevName = selectBodyView(store.state, prevCentre).name;
    const newName = selectBodyView(store.state, target.id).name;
    store.dispatch({ type: 'SwitchCentre', body: target.id });
    announce?.(describeCentreSwitch(prevName, newName));
  }

  // "Make centre" single-pointer / keyboard alternative to drop-on-centre (AC 3).
  function makeCentre(): void {
    if (!phaseControllable) return;
    switchCentre(store.state.selectedBody);
  }

  // The always-visible "Central body" control (AC 1): a native <select> of all
  // nine bodies. `onchange` only fires on a real change, and `switchCentre`
  // no-ops on the current centre, so reselecting the centre is never state-changing.
  function onCentreSelect(event: Event): void {
    switchCentre((event.currentTarget as HTMLSelectElement).value);
  }

  // Equal-time-sector overlay (Story 2.1, FR-9). SHAREABLE store state read via the
  // selector (AD-2 layer table; no `core` import in ui). The count range comes from
  // the `core` constants re-exported through `state`, so the UI never re-declares it.
  const sectors = $derived(selectSectorSettings(store.state));

  function toggleSectors(event: Event): void {
    const value = (event.currentTarget as HTMLInputElement).checked;
    store.dispatch({ type: 'SetSectorOverlayVisible', value });
    announce?.(
      value
        ? `Equal-time sectors on for ${selected.name}, ${sectors.count} sectors.`
        : `Equal-time sectors off for ${selected.name}.`
    );
  }

  function applySectorCount(count: number): void {
    if (!Number.isFinite(count)) return;
    // The reducer rounds and clamps to [2, 24], so an out-of-range value cannot be
    // applied (AC 4). Read the authoritative value back for the announcement.
    store.dispatch({ type: 'SetSectorCount', count });
    announce?.(`Sector count ${selectSectorSettings(store.state).count}.`);
  }

  function onSectorSliderInput(event: Event): void {
    applySectorCount(Number((event.currentTarget as HTMLInputElement).value));
  }

  // Typed count field: mirror the semi-major draft/commit pattern so a reactive
  // value binding does not stomp multi-digit entry (e.g. typing "24"). Commit on
  // blur/Enter; reconcile the draft to the authoritative clamped value afterwards,
  // so an out-of-range entry (e.g. 30) visibly snaps to the clamp (24).
  let countFieldFocused = $state(false);
  let countDraft = $state('');

  $effect(() => {
    if (!countFieldFocused) countDraft = String(sectors.count);
  });

  function commitSectorCountField(): void {
    const text = countDraft.trim();
    if (text !== '') applySectorCount(Number(text));
    countDraft = String(selectSectorSettings(store.state).count);
  }

  function onSectorCountFieldKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      commitSectorCountField();
    }
  }

  // Eccentricity clamp bounds come from the canonical `core/ranges.ts` via the
  // `state` re-export (AD-2 layer table: ui depends on state only), so the slider
  // clamp names the same [0, 0.95] range as the reducer with no local literal.
  const MIN_E = ECCENTRICITY_MIN;
  const MAX_E = ECCENTRICITY_MAX;

  // Log-scaled semi-major-axis slider: the 0.01-100 AU range spans four orders of
  // magnitude, so the slider operates in log10(AU) space (AC 1); the paired typed
  // field edits AU linearly. Both endpoints land exactly on the permitted bounds.
  const MIN_A_LOG = Math.log10(MIN_SEMI_MAJOR_AXIS_AU); // -2
  const MAX_A_LOG = Math.log10(MAX_SEMI_MAJOR_AXIS_AU); // 2

  const fmtE = (e: number): string => e.toFixed(2);

  // Descriptive aria-valuetext / live announcement (AC-8), e.g. "0.50, elliptical".
  const describeE = (e: number): string => `${fmtE(e)}, ${describeOrbitShape(e)}`;
  const valueText = $derived(describeE(readouts.e));

  // Live semi-major axis in AU for the editor; 0 for the (disabled) central body.
  const aAu = $derived(isCentral ? 0 : metresToAu(readouts.a));
  // Slider position in log10(AU) space, clamped so a 0-metres central body cannot
  // produce -Infinity while the control is disabled.
  const aLog = $derived(
    isCentral ? MIN_A_LOG : Math.log10(Math.max(MIN_SEMI_MAJOR_AXIS_AU, aAu))
  );
  const aAuText = $derived(isCentral ? '' : aAu.toFixed(2));
  const aValueText = $derived(formatAu(readouts.a));

  // Finding 1 fix: the typed AU field must NOT be driven directly by the derived
  // store value while the learner is typing, or the reactive `value={aAuText}`
  // overwrites the DOM input mid-keystroke and corrupts multi-digit entry. Instead
  // we keep a local draft string bound to the field, dispatch only on commit
  // (blur/Enter, never per keystroke), and refresh the draft from the authoritative
  // value ONLY while the field is unfocused. The slider keeps live dispatch; because
  // it refreshes the draft only when the field is blurred, slider and typed field
  // never stomp each other's in-progress value.
  let aFieldFocused = $state(false);
  let aDraft = $state('');

  // Read the authoritative AU field text from a direct selector call (not the
  // derived, which can read stale synchronously right after a dispatch), mirroring
  // how `applyAu` recomputes its announcement.
  function currentAuText(): string {
    const r = selectOrbitalReadouts(store.state, store.state.selectedBody);
    return r.isCentral ? '' : metresToAu(r.a).toFixed(2);
  }

  // Keep the draft synced with the authoritative value whenever the field is not
  // being edited: body switch, slider drag, reset, or a rejected/blank commit that
  // must revert. While focused, the learner's in-progress text is left untouched.
  $effect(() => {
    if (!aFieldFocused) aDraft = aAuText;
  });

  function onEccentricityInput(event: Event): void {
    const raw = Number((event.currentTarget as HTMLInputElement).value);
    if (!Number.isFinite(raw)) return;
    const clamped = Math.min(MAX_E, Math.max(MIN_E, raw));
    store.dispatch({ type: 'SetEccentricity', body: store.state.selectedBody, value: clamped });
    announce?.(`Eccentricity ${describeE(clamped)}`);
  }

  // Apply a semi-major axis given in AU. REJECT, NEVER CLAMP (AC 6): a non-finite
  // value or one outside [0.01, 100] AU is not dispatched, so the last valid value
  // stays active, and the permitted range is announced through the live region.
  function applyAu(au: number): void {
    if (
      !Number.isFinite(au) ||
      au < MIN_SEMI_MAJOR_AXIS_AU ||
      au > MAX_SEMI_MAJOR_AXIS_AU
    ) {
      announce?.(SEMI_MAJOR_AXIS_RANGE_MESSAGE);
      return;
    }
    // Convert AU -> SI metres exactly once, here at the ui boundary (AD-5).
    store.dispatch({
      type: 'SetSemiMajorAxis',
      body: store.state.selectedBody,
      value: auToMetres(au)
    });
    const r = selectOrbitalReadouts(store.state, store.state.selectedBody);
    announce?.(`Semi-major axis ${formatAu(r.a)}, orbital period ${formatPeriod(r.period)}.`);
  }

  function onSemiMajorSliderInput(event: Event): void {
    // Slider value is log10(AU); min/max bound it to the permitted range.
    const logAu = Number((event.currentTarget as HTMLInputElement).value);
    applyAu(10 ** logAu);
  }

  function onSemiMajorFieldFocus(): void {
    aFieldFocused = true;
  }

  // Commit the typed draft: dispatch only here (blur/Enter), then reconcile the
  // draft to the authoritative last-valid AU value so a rejected, out-of-range, or
  // blank entry reverts and a valid one is echoed back normalised (AC 6).
  function commitSemiMajorField(): void {
    const text = aDraft.trim();
    if (text !== '') applyAu(Number(text));
    aDraft = currentAuText();
  }

  function onSemiMajorFieldBlur(): void {
    aFieldFocused = false;
    commitSemiMajorField();
  }

  function onSemiMajorFieldKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      commitSemiMajorField();
    }
  }
</script>

<aside
  class="detail glass-panel"
  aria-label="Selected body detail"
  data-testid="detail-panel"
  data-selected-body={selected.id}
  data-phase-offset={phaseOffset}
>
  <div class="detail-header">
    <div class="titles">
      <span class="label-caps kicker">Selected</span>
      <h2 class="detail-title" data-testid="detail-title">{selected.name}</h2>
    </div>
    <button
      type="button"
      class="collapse-btn"
      data-testid="detail-collapse"
      aria-expanded={!collapsed}
      aria-label={collapsed ? 'Expand selected body detail' : 'Collapse selected body detail'}
      onclick={() => (collapsed = !collapsed)}
    >
      <span class="material-symbols-outlined" aria-hidden="true"
        >{collapsed ? 'expand_more' : 'expand_less'}</span
      >
    </button>
  </div>

  {#if !collapsed}
    <dl class="detail-body" data-testid="detail-body">
      <div class="row">
        <dt class="label-caps">Selected body</dt>
        <dd>
          <span class="marker" style={`--marker-colour: ${selected.colour}`} aria-hidden="true"
          ></span>
          {selected.name}
        </dd>
      </div>
      <div class="row">
        <dt class="label-caps">Central body</dt>
        <dd>
          <span class="marker" style={`--marker-colour: ${central.colour}`} aria-hidden="true"
          ></span>
          {central.name}
        </dd>
      </div>
    </dl>

    <!-- Removed-body status note (Story 4.1 collided / Story 4.2 absorbed, AC 4, 5):
         the removed body stays selected with read-only controls; mass-neutral wording
         distinguishing central absorption from a body-body collision. -->
    {#if isRemoved}
      <p class="removed-note" data-testid="removed-note" role="note">
        <span class="chip label-caps removed-chip" data-testid="removed-status-chip">
          {selected.status === 'collided'
            ? 'Collided'
            : selected.status === 'absorbed'
              ? 'Absorbed'
              : 'Removed'}
        </span>
        {#if selected.status === 'absorbed'}
          Absorbed by the centre. In this model that is illustrative only and changes
          no other orbit. Controls are read-only. Select a surviving body to edit.
        {:else}
          Removed by collision. Controls are read-only. Select a surviving body to edit.
        {/if}
      </p>
    {/if}

    <!-- Central-body control (Story 2.2, AC 1, 3): pick any of the nine bodies as
         the centre. Always visible (even when the centre is the selected body). A
         native <select> is keyboard-operable and screen-reader-labelled by default
         (AD-8). One-way value + onchange, mirroring the sliders, so a switch does
         not fight the reactive value. Reselecting the current centre is a no-op. -->
    <section class="control-group" data-testid="central-body-control" aria-label="Central body control">
      <div class="control-head">
        <label class="label-caps" for="central-body-select">Central body</label>
        <span class="chip label-caps centre-chip" data-testid="central-current">{central.name}</span>
      </div>
      <select
        id="central-body-select"
        class="numeric-field select"
        value={central.id}
        data-testid="central-body-select"
        onchange={onCentreSelect}
      >
        {#each allBodies as body (body.id)}
          <option value={body.id} data-testid={`central-option-${body.id}`}>
            {body.name}{body.isCentral ? ' (centre)' : ''}
          </option>
        {/each}
      </select>
      <p class="explain">
        Choose the body at the focus. Making a lighter body the centre is an
        experiment, not a realistic Solar System.
      </p>
    </section>

    <!-- Semi-major-axis editor (Story 1.4, AC 1, 2, 5, 6, 7). Log-scaled slider +
         typed AU field; reject-not-clamp validation; disabled/not-applicable for
         the central body. -->
    <section
      class="control-group"
      class:disabled={isReadOnly}
      data-testid="semimajor-control"
      aria-label="Semi-major axis control"
    >
      <div class="control-head">
        <label class="label-caps" for="semimajor-slider">Semi-major axis</label>
        {#if isCentral}
          <span class="chip label-caps na-chip" data-testid="semimajor-na">Not applicable</span>
        {:else}
          <span class="data-mono value" data-testid="semimajor-value">{aValueText}</span>
        {/if}
      </div>

      {#if isCentral}
        <p class="explain" data-testid="semimajor-na-note">
          The central body does not orbit, so orbital distance does not apply to it.
        </p>
      {/if}

      <input
        id="semimajor-slider"
        class="slider"
        type="range"
        min={MIN_A_LOG}
        max={MAX_A_LOG}
        step="0.01"
        value={aLog}
        disabled={isReadOnly}
        aria-valuetext={aValueText}
        data-testid="semimajor-slider"
        oninput={onSemiMajorSliderInput}
      />

      <div class="numeric-row">
        <label class="numeric-label" for="semimajor-number">Value (AU)</label>
        <input
          id="semimajor-number"
          class="numeric-field data-mono"
          type="text"
          inputmode="decimal"
          bind:value={aDraft}
          disabled={isReadOnly}
          aria-label="Semi-major axis in astronomical units"
          data-testid="semimajor-number"
          onfocus={onSemiMajorFieldFocus}
          onblur={onSemiMajorFieldBlur}
          onkeydown={onSemiMajorFieldKeydown}
        />
      </div>

      <p class="explain">
        Semi-major axis: half the orbit's longest width, used here as orbital distance.
        Editable from 0.01 to 100 AU.
      </p>
    </section>

    <!-- Eccentricity control (AC 2, 3, 7, 8). -->
    <section
      class="control-group"
      class:disabled={isReadOnly}
      data-testid="eccentricity-control"
      aria-label="Eccentricity control"
    >
      <div class="control-head">
        <label class="label-caps" for="eccentricity-slider">Eccentricity</label>
        {#if isCentral}
          <span class="chip label-caps na-chip" data-testid="eccentricity-na">Not applicable</span>
        {:else}
          <span class="data-mono value" data-testid="eccentricity-value">{fmtE(readouts.e)}</span>
        {/if}
      </div>

      {#if isCentral}
        <p class="explain" data-testid="eccentricity-na-note">
          The central body does not orbit, so eccentricity does not apply to it.
        </p>
      {/if}

      <input
        id="eccentricity-slider"
        class="slider"
        type="range"
        min={MIN_E}
        max={MAX_E}
        step="0.01"
        value={readouts.e}
        disabled={isReadOnly}
        aria-valuetext={valueText}
        data-testid="eccentricity-slider"
        oninput={onEccentricityInput}
      />

      <div class="numeric-row">
        <label class="numeric-label" for="eccentricity-number">Value</label>
        <input
          id="eccentricity-number"
          class="numeric-field data-mono"
          type="number"
          min={MIN_E}
          max={MAX_E}
          step="0.01"
          value={readouts.e}
          disabled={isReadOnly}
          data-testid="eccentricity-number"
          oninput={onEccentricityInput}
        />
      </div>

      <p class="explain">
        Eccentricity: a number describing how stretched an orbit is. Zero is circular.
      </p>
    </section>

    <!-- Equal-time-sector overlay (Story 2.1, FR-9, AC 1, 4, 5, 6). Toggle + count
         act on the SELECTED orbit; Not applicable for the central or a removed
         body (dispatches nothing in that case). -->
    <section
      class="control-group"
      class:disabled={!sectors.applicable}
      data-testid="sector-control"
      aria-label="Equal-time sector overlay control"
    >
      <div class="control-head">
        <span class="label-caps">Equal-time sectors</span>
        {#if !sectors.applicable}
          <span class="chip label-caps na-chip" data-testid="sector-na">Not applicable</span>
        {:else}
          <span class="data-mono value" data-testid="sector-count-value">{sectors.count}</span>
        {/if}
      </div>

      {#if sectors.applicable}
        <label class="sector-toggle">
          <input
            type="checkbox"
            checked={sectors.visible}
            data-testid="sector-toggle"
            onchange={toggleSectors}
          />
          <span>Show equal-time sectors</span>
        </label>

        <div class="numeric-row">
          <label class="numeric-label" for="sector-count-slider">Number of sectors</label>
          <input
            id="sector-count-slider"
            class="slider"
            type="range"
            min={MIN_SECTOR_COUNT}
            max={MAX_SECTOR_COUNT}
            step="1"
            value={sectors.count}
            disabled={!sectors.visible}
            aria-valuetext={`${sectors.count} sectors`}
            data-testid="sector-count-slider"
            oninput={onSectorSliderInput}
          />
          <input
            id="sector-count"
            class="numeric-field data-mono"
            type="text"
            inputmode="numeric"
            bind:value={countDraft}
            disabled={!sectors.visible}
            aria-label="Number of equal-time sectors, 2 to 24"
            data-testid="sector-count"
            onfocus={() => (countFieldFocused = true)}
            onblur={() => {
              countFieldFocused = false;
              commitSectorCountField();
            }}
            onkeydown={onSectorCountFieldKeydown}
          />
        </div>

        <p class="explain" data-testid="sector-explain">
          Equal-time sectors: each wedge is the area the body sweeps in one equal
          slice of time. The wedges look different but their areas are about equal,
          so the body must move faster near periapsis and slower near apoapsis.
        </p>
      {:else}
        <p class="explain" data-testid="sector-na-note">
          {#if isCentral}
            The central body does not orbit, so equal-time sectors do not apply to it.
          {:else}
            This body has left the simulation, so equal-time sectors do not apply to it.
          {/if}
        </p>
      {/if}
    </section>

    <!-- Phase manipulation (Story 1.7, FR-19): the keyboard + single-pointer
         alternatives to the canvas drag, satisfying WCAG 2.2 SC 2.5.7. The step
         buttons are the single-pointer no-drag path; they are also keyboard
         operable (Enter/Space), and Left/Right arrows step within this group. -->
    <section
      class="control-group"
      class:disabled={!phaseControllable}
      data-testid="phase-control"
      aria-label="Orbital phase control"
    >
      <div class="control-head">
        <span class="label-caps">Orbital phase</span>
        {#if !phaseControllable}
          <span class="chip label-caps na-chip" data-testid="phase-na">Not applicable</span>
        {/if}
      </div>

      {#if phaseControllable}
        <div class="phase-buttons" role="group" aria-label="Step orbital phase">
          <button
            type="button"
            class="phase-btn"
            data-testid="phase-step-back"
            aria-label={`Step ${selected.name} back along its orbit`}
            onclick={() => stepPhase(-1)}
            onkeydown={onPhaseKeydown}
          >
            <span aria-hidden="true">− Step back</span>
          </button>
          <button
            type="button"
            class="phase-btn"
            data-testid="phase-step-forward"
            aria-label={`Step ${selected.name} forward along its orbit`}
            onclick={() => stepPhase(1)}
            onkeydown={onPhaseKeydown}
          >
            <span aria-hidden="true">Step forward +</span>
          </button>
        </div>
        <button
          type="button"
          class="phase-btn make-centre"
          data-testid="make-centre"
          aria-label={`Make ${selected.name} the central body`}
          onclick={makeCentre}
        >
          Make centre
        </button>
        <p class="explain">
          Move the body around its orbit without changing its shape or size. Drag it on the
          canvas, use these buttons, or press the Left and Right arrow keys here.
        </p>
      {:else}
        <p class="explain" data-testid="phase-na-note">
          {#if isCentral}
            The central body does not orbit, so its orbital phase cannot be changed.
          {:else}
            This body has left the simulation, so its orbital phase cannot be changed.
          {/if}
        </p>
      {/if}
    </section>

    <!-- SI orbital readouts, converted at the ui boundary (AC 3, 4, 5). -->
    {#if !isCentral}
      <dl class="readouts" data-testid="orbital-readouts">
        <div class="row">
          <dt class="label-caps">Semi-major axis</dt>
          <dd class="data-mono" data-testid="readout-semimajor">{formatAu(readouts.a)}</dd>
        </div>
        <div class="row">
          <dt class="label-caps">Periapsis</dt>
          <dd class="data-mono" data-testid="readout-periapsis">{formatAu(readouts.periapsis)}</dd>
          <p class="explain">Periapsis: the closest point of the orbit to the central body.</p>
        </div>
        <div class="row">
          <dt class="label-caps">Apoapsis</dt>
          <dd class="data-mono" data-testid="readout-apoapsis">{formatAu(readouts.apoapsis)}</dd>
          <p class="explain">Apoapsis: the farthest point of the orbit from the central body.</p>
        </div>
        <div class="row">
          <dt class="label-caps">Current distance</dt>
          <dd class="data-mono" data-testid="readout-distance">{formatAu(readouts.distance)}</dd>
        </div>
        <div class="row">
          <dt class="label-caps">Current speed</dt>
          <dd class="data-mono" data-testid="readout-speed">{formatKmPerSec(readouts.speed)}</dd>
        </div>
        <div class="row">
          <dt class="label-caps">Orbital period</dt>
          <dd class="data-mono" data-testid="readout-period">{formatPeriod(readouts.period)}</dd>
          <p class="explain">Orbital period: the time to complete one orbit. The further out the orbit, the longer its year.</p>
        </div>
      </dl>
    {/if}
  {/if}
</aside>

<style>
  .detail {
    position: absolute;
    top: calc(var(--top-bar-height) + var(--edge-margin));
    right: var(--edge-margin);
    width: var(--detail-panel-width);
    max-height: calc(100vh - var(--top-bar-height) - var(--edge-margin) * 2);
    overflow-y: auto;
    padding: var(--panel-padding);
    z-index: 8;
  }

  .detail-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  .kicker {
    color: var(--primary);
  }

  .detail-title {
    margin: 4px 0 0;
    font-size: 24px;
    font-weight: 600;
  }

  .collapse-btn {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: none;
    border-radius: var(--radius-full);
    background: rgba(255, 255, 255, 0.08);
    color: var(--on-surface);
    cursor: pointer;
  }

  .detail-body {
    margin: 16px 0 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .row {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  dt {
    color: var(--on-surface-variant);
  }

  dd {
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
  }

  .marker {
    width: 12px;
    height: 12px;
    border-radius: var(--radius-full);
    background: var(--marker-colour);
    box-shadow: 0 0 5px var(--marker-colour);
  }

  .control-group {
    margin: 20px 0 0;
    padding-top: 16px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .control-group.disabled {
    opacity: 0.5;
  }

  .control-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .value {
    font-size: 16px;
  }

  .chip {
    padding: 3px 8px;
    border-radius: var(--radius-full);
  }

  .na-chip {
    background: rgba(255, 255, 255, 0.1);
    color: var(--on-surface-variant);
  }

  .removed-note {
    margin: 16px 0 0;
    padding: 10px 12px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    line-height: 1.4;
    color: var(--on-surface-variant);
    border-radius: var(--radius-default);
    border: 1px solid rgba(255, 255, 255, 0.12);
    background: rgba(255, 255, 255, 0.05);
  }

  .removed-chip {
    padding: 3px 8px;
    border-radius: var(--radius-full);
    background: rgba(255, 255, 255, 0.16);
    color: var(--on-surface);
  }

  .centre-chip {
    background: var(--secondary-container, rgba(255, 255, 255, 0.12));
    color: var(--on-secondary-container, var(--on-surface));
  }

  .select {
    width: 100%;
    cursor: pointer;
  }

  .select:focus-visible {
    outline: 2px solid #d4e3ff;
    outline-offset: 2px;
  }

  .slider {
    width: 100%;
    accent-color: var(--primary);
    cursor: pointer;
  }

  .control-group.disabled .slider,
  .control-group.disabled .numeric-field {
    cursor: not-allowed;
  }

  .slider:focus-visible,
  .numeric-field:focus-visible {
    outline: 2px solid #d4e3ff;
    outline-offset: 2px;
  }

  .numeric-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .numeric-label {
    font-size: 13px;
    color: var(--on-surface-variant);
  }

  .numeric-field {
    width: 88px;
    padding: 6px 8px;
    border-radius: var(--radius-default);
    border: 1px solid var(--outline-variant, #414751);
    background: rgba(255, 255, 255, 0.04);
    color: var(--on-surface);
    font-size: 14px;
  }

  .explain {
    margin: 0;
    font-size: 13px;
    line-height: 1.4;
    color: var(--on-surface-variant);
  }

  .readouts {
    margin: 20px 0 0;
    padding-top: 16px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .sector-toggle {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: var(--on-surface);
    cursor: pointer;
  }

  .sector-toggle input {
    accent-color: var(--primary);
  }

  .phase-buttons {
    display: flex;
    gap: 8px;
  }

  .phase-btn {
    flex: 1 1 auto;
    padding: 8px 12px;
    border-radius: var(--radius-default);
    border: 1px solid var(--outline-variant, #414751);
    background: rgba(255, 255, 255, 0.06);
    color: var(--on-surface);
    font-size: 14px;
    cursor: pointer;
  }

  .phase-btn.make-centre {
    width: 100%;
  }

  .phase-btn:hover {
    background: rgba(255, 255, 255, 0.12);
  }

  .phase-btn:focus-visible {
    outline: 2px solid #d4e3ff;
    outline-offset: 2px;
  }

  .control-group.disabled .phase-btn {
    cursor: not-allowed;
  }
</style>
